/**
 * Safe ISO string parsing utilities
 * Handles the common pattern of appending 'Z' to ISO strings from GraphQL
 */
import dayjs, { type Dayjs } from './dayjs-config';

/**
 * Safely parse an ISO string, automatically handling the 'Z' suffix pattern
 *
 * GraphQL often returns ISO strings without the 'Z' suffix for UTC times.
 * This utility automatically appends 'Z' if needed.
 *
 * @param isoString - ISO 8601 date string (with or without 'Z' suffix)
 * @returns dayjs instance in UTC, or null if invalid
 *
 * @example
 * parseISOSafe('2024-01-15T10:30:00') // Treats as UTC
 * parseISOSafe('2024-01-15T10:30:00Z') // Already UTC
 * parseISOSafe(null) // Returns null
 */
export function parseISOSafe(isoString: string | null | undefined): Dayjs | null {
  if (!isoString) return null;

  try {
    // If the string ends with 'Z', it's already UTC
    if (isoString.endsWith('Z')) {
      const parsed = dayjs.utc(isoString);
      return parsed.isValid() ? parsed : null;
    }

    // Otherwise, append 'Z' to treat as UTC
    const parsed = dayjs.utc(isoString + 'Z');
    return parsed.isValid() ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Parse an ISO string and return a Date object
 * Useful for compatibility with components expecting Date objects
 *
 * @param isoString - ISO 8601 date string
 * @returns Date object or null if invalid
 */
export function parseISOToDate(isoString: string | null | undefined): Date | null {
  const parsed = parseISOSafe(isoString);
  return parsed ? parsed.toDate() : null;
}

/**
 * Convert a value to dayjs, handling multiple input types
 *
 * @param value - Date, dayjs, ISO string, or timestamp
 * @returns dayjs instance or null if invalid
 */
export function toDayjs(value: Date | Dayjs | string | number | null | undefined): Dayjs | null {
  if (!value) return null;

  try {
    // Already a dayjs instance
    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value : null;
    }

    // Date object
    if (value instanceof Date) {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }

    // String - try ISO parsing first
    if (typeof value === 'string') {
      return parseISOSafe(value);
    }

    // Number (timestamp)
    if (typeof value === 'number') {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }

    return null;
  } catch {
    return null;
  }
}
